// The socket.
//
// It carries JSON both ways and reconnects with a backoff. The client is never
// authoritative about anything. A reconnect keeps only the current held input;
// the next snapshot is the truth for everything else.

import { SOCKET_CLOSE, type ClientMessage, type ServerMessage } from "../shared/protocol";

export type NetHandler = (msg: ServerMessage) => void;

/** First retry delay, and the ceiling the doubling stops at. */
const RECONNECT_BASE_MS = 400;
const RECONNECT_MAX_MS = 8000;

export class Net {
  private ws: WebSocket | null = null;
  private handler: NetHandler;
  private retry = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;

  /** Desired and last-sent movement are separate so the desired state survives
   *  a reconnect, while each new socket is always told what it is. */
  private wantedMx = 0;
  private wantedMy = 0;
  private sentMx: number | undefined;
  private sentMy: number | undefined;

  constructor(handler: NetHandler) {
    this.handler = handler;
  }

  connect(): void {
    this.stopped = false;
    const scheme = location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(`${scheme}://${location.host}/ws`);
    this.ws = ws;
    ws.addEventListener("open", () => {
      if (ws !== this.ws) return;
      this.retry = 0;
      this.sentMx = undefined;
      this.sentMy = undefined;
      this.flushIntent();
    });
    ws.addEventListener("message", (ev) => {
      if (ws !== this.ws) return;
      if (typeof ev.data !== "string") return;
      try {
        this.handler(JSON.parse(ev.data) as ServerMessage);
      } catch {
        /* a frame we cannot read is a frame we ignore */
      }
    });
    ws.addEventListener("close", (ev) => {
      if (ws !== this.ws) return;
      this.ws = null;
      // These are final answers. Reconnecting would either hammer a full world
      // or make two tabs repeatedly supersede one another.
      if (ev.code === SOCKET_CLOSE.superseded || ev.code === SOCKET_CLOSE.characterGone || ev.code === SOCKET_CLOSE.full) {
        this.stopped = true;
        return;
      }
      this.scheduleReconnect();
    });
    ws.addEventListener("error", () => {
      if (ws === this.ws) ws.close();
    });
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.timer) return;
    // Backoff, capped and jittered: a world that is full or down should not be
    // hammered by every browser that ever had it open.
    //
    // The jitter is a proportion of the delay rather than a fixed few hundred
    // milliseconds, because the case that matters is the far end of the curve.
    // A deploy closes every socket in the same instant, so every client walks
    // the same ladder together; at the eight-second cap a flat 250 ms spreads
    // that herd across three percent of a window and it arrives as a spike
    // anyway. Half the delay, uniformly, turns the herd back into a queue.
    const ceiling = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** this.retry++);
    const delay = ceiling * (0.5 + Math.random() * 0.5);
    this.timer = setTimeout(() => {
      this.timer = null;
      this.connect();
    }, delay);
  }

  close(): void {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.ws?.close();
    this.ws = null;
  }

  get open(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /** Put a message on the wire. Returns whether it actually went. */
  send(msg: ClientMessage): boolean {
    if (!this.open) return false;
    this.ws?.send(JSON.stringify(msg));
    return true;
  }

  /** Remember the current movement intent and send it only when it changed.
   *
   *  What was sent is recorded only once it has genuinely left the machine.
   *  The desired state is separate, so a reconnect always sends even a zero:
   *  otherwise the server can keep walking a body whose key-up was lost. */
  sendIntent(mx: number, my: number): void {
    this.wantedMx = mx;
    this.wantedMy = my;
    this.flushIntent();
  }

  /** Send the current intent again even if it has not changed. The world
   *  drops every held input when a maintenance lock starts, so a key still
   *  held when it ends has to be said again. */
  resyncIntent(): void {
    this.sentMx = undefined;
    this.sentMy = undefined;
    this.flushIntent();
  }

  private flushIntent(): void {
    if (this.wantedMx === this.sentMx && this.wantedMy === this.sentMy) return;
    if (!this.send({ t: "input", mx: this.wantedMx, my: this.wantedMy })) return;
    this.sentMx = this.wantedMx;
    this.sentMy = this.wantedMy;
  }
}
